/**
 * Pure, SSR-safe helpers for the kitchen kiosk/fullscreen mode.
 * No browser globals are touched at module scope or during plain derivation:
 * `fullscreenApiSupported()` guards `document` and returns `false` on the
 * server, so render tests and SSR never crash.
 */

export const KITCHEN_KIOSK_PATH = '/dashboard/kitchen';

export const FULLSCREEN_ENTER_LABEL = 'Entrar en pantalla completa';
export const FULLSCREEN_EXIT_LABEL = 'Salir de pantalla completa';
export const KIOSK_EXIT_LABEL = 'Salir del modo kiosk';
export const KIOSK_ENTER_LABEL = 'Abrir modo kiosk';

/** `?kiosk=1` enables immersive kiosk mode; any other value keeps normal mode. */
export function isKioskSearch(search: string | URLSearchParams | null | undefined): boolean {
  if (!search) return false;
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  return params.get('kiosk') === '1';
}

/** Kiosk mode only applies to the kitchen route itself, never to other dashboard routes. */
export function isKioskLocation(pathname: string, search: string | URLSearchParams | null | undefined): boolean {
  return pathname === KITCHEN_KIOSK_PATH && isKioskSearch(search);
}

/** Label mapping for the fullscreen toggle based on current fullscreen state. */
export function fullscreenButtonLabel(isFullscreen: boolean): string {
  return isFullscreen ? FULLSCREEN_EXIT_LABEL : FULLSCREEN_ENTER_LABEL;
}

/** True only in browsers where the Fullscreen API is present and enabled. */
export function fullscreenApiSupported(): boolean {
  if (typeof document === 'undefined') return false;
  return document.fullscreenEnabled === true && typeof document.documentElement.requestFullscreen === 'function';
}

/** Normal dashboard kitchen path, without immersive kiosk query params. */
export function normalKitchenPath(): string {
  return KITCHEN_KIOSK_PATH;
}

/** Immersive kitchen path used by dashboard links and kiosk entry affordances. */
export function kioskKitchenPath(): string {
  return `${KITCHEN_KIOSK_PATH}?kiosk=1`;
}
